import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { resolveBarColor } from '../../utils/formatMetricValue';
import StatusBadge from './StatusBadge';
import TileMetaActions from '../TileMetaActions';
import CatalogIdBadge from '../CatalogIdBadge';
import { getMetricDisplayName } from './rendererTypes';
import type { MetricRendererProps } from './rendererTypes';
import { tileMetaTitlePad } from '../../utils/tileMeta';

export default function DataTableRenderer({ config, data, span }: MetricRendererProps) {
  const isFullSpan = span === 'full';
  const theme = useTheme();
  const columns = data.table?.columns ?? [];
  const rows = data.table?.rows ?? [];
  const title = getMetricDisplayName(data, config);
  const modalPadX = 2.5;
  const modalCellSx = { px: modalPadX, py: 1.25 };
  const headerLabelSx = {
    fontWeight: 700,
    fontSize: 11,
    color: 'text.secondary',
    letterSpacing: '0.04em',
    ...(isFullSpan ? modalCellSx : {}),
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: isFullSpan ? 0 : 2,
        border: isFullSpan ? 'none' : `1px solid ${theme.palette.divider}`,
        overflow: isFullSpan ? 'visible' : 'auto',
        width: '100%',
        position: 'relative',
      }}
    >
      {!isFullSpan ? (
        <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} placement="corner" />
      ) : null}
      {!isFullSpan && title ? (
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            px: 2,
            pt: 2,
            pb: 1,
            pr: tileMetaTitlePad(data.meta, 2),
          }}
        >
          {title}
        </Typography>
      ) : null}
      {isFullSpan ? <CatalogIdBadge registry={data.meta?.registry} /> : null}
      <Table size="small">
        <TableHead
          sx={{
            position: 'sticky',
            top: isFullSpan ? 0 : title ? 44 : 0,
            bgcolor: 'background.paper',
            zIndex: isFullSpan ? 3 : 2,
          }}
        >
          {isFullSpan && title ? (
            <TableRow>
              <TableCell colSpan={Math.max(columns.length, 1)} sx={{ border: 0, p: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: modalPadX,
                    pt: 1.5,
                    pb: 0.25,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, color: 'text.primary', flex: 1, minWidth: 0, lineHeight: 1.35 }}
                  >
                    {title}
                  </Typography>
                  <TileMetaActions
                    query={data.meta?.query}
                    registry={data.meta?.registry}
                    placement="inline"
                    showCatalogId={false}
                  />
                </Box>
              </TableCell>
            </TableRow>
          ) : null}
          {isFullSpan && !title ? (
            <TableRow>
              <TableCell colSpan={Math.max(columns.length, 1)} sx={{ border: 0, p: 0 }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: modalPadX,
                    pt: 1.5,
                    pb: 0.25,
                  }}
                >
                  <TileMetaActions
                    query={data.meta?.query}
                    registry={data.meta?.registry}
                    placement="inline"
                    showCatalogId={false}
                  />
                </Box>
              </TableCell>
            </TableRow>
          ) : null}
          <TableRow sx={isFullSpan ? { '& .MuiTableCell-root': { pt: 0.5 } } : undefined}>
            {columns.map(col => (
              <TableCell key={col.key} sx={headerLabelSx}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={String(row.label ?? row.salesperson ?? rowIndex)} hover>
              {columns.map(col => {
                const raw = row[col.key];
                if (col.type === 'badge') {
                  const tone = (row.flagTone as 'success' | 'warning' | 'info' | 'neutral') || 'neutral';
                  return (
                    <TableCell key={col.key} sx={isFullSpan ? modalCellSx : undefined}>
                      <StatusBadge badge={{ label: String(raw ?? ''), tone }} />
                    </TableCell>
                  );
                }
                if (col.type === 'trend') {
                  const trendKey = `${col.key}Trend`;
                  const trendDir = row[trendKey] as string | undefined;
                  return (
                    <TableCell key={col.key} sx={isFullSpan ? modalCellSx : undefined}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">{String(raw ?? '')}</Typography>
                        {trendDir === 'up' ? (
                          <TrendingUpIcon sx={{ fontSize: 14, color: 'success.main' }} />
                        ) : trendDir === 'down' ? (
                          <TrendingDownIcon sx={{ fontSize: 14, color: 'error.main' }} />
                        ) : null}
                      </Box>
                    </TableCell>
                  );
                }
                if (col.type === 'bar') {
                  const share = Number(row.share ?? raw ?? 0);
                  const display =
                    typeof raw === 'string'
                      ? raw
                      : col.key === 'share'
                        ? `${share}%`
                        : String(raw ?? share);
                  const barColor = resolveBarColor(
                    (row.barColor as string) || 'primary',
                    theme.palette,
                  );
                  return (
                    <TableCell key={col.key} sx={{ minWidth: 120, ...(isFullSpan ? modalCellSx : {}) }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {display}
                      </Typography>
                      {share > 0 ? (
                        <Box
                          sx={{
                            height: 6,
                            borderRadius: 1,
                            backgroundColor: theme.palette.action.hover,
                            overflow: 'hidden',
                          }}
                        >
                          <Box
                            sx={{
                              width: `${Math.min(100, share)}%`,
                              height: '100%',
                              backgroundColor: barColor,
                            }}
                          />
                        </Box>
                      ) : null}
                    </TableCell>
                  );
                }
                const highlight =
                  col.key === 'percentMarkedDead' && Boolean(row.highlightMarkedDead);
                return (
                  <TableCell key={col.key} sx={isFullSpan ? modalCellSx : undefined}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: highlight ? 700 : 400,
                        color: highlight ? 'error.main' : 'text.primary',
                      }}
                    >
                      {raw == null ? '—' : String(raw)}
                      {col.key === 'newThisMonth' && row.newChangeLabel
                        ? ` (${row.newChangeLabel})`
                        : ''}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}
